import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Cropper from "react-easy-crop";
import { Dialog, DialogActions, DialogContent, DialogTitle, Slider } from "@material-ui/core";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import { IMAGE_ASPECT_RATIO } from "../utils";

/**
 * Interactive cropper locked to a 3:1 aspect ratio. The default framing is
 * already valid, so the user can just press "Apply" without adjusting anything.
 */
function ImageCropModal({ open, imageSrc, onCancel, onApply, isProcessing }) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    // Reset the view every time a new image is loaded.
    useEffect(() => {
        if (open) {
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedAreaPixels(null);
        }
    }, [open, imageSrc]);

    const onCropComplete = useCallback((_, areaPixels) => {
        setCroppedAreaPixels(areaPixels);
    }, []);

    return (
        <Dialog open={open} maxWidth="md" fullWidth aria-labelledby="campaign-image-crop-title">
            <DialogTitle id="campaign-image-crop-title">Adjust the banner (3:1)</DialogTitle>
            <DialogContent>
                <div className="campaign-cropper">
                    {imageSrc && (
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={IMAGE_ASPECT_RATIO}
                            restrictPosition
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onCropComplete}
                        />
                    )}
                </div>
                <div className="campaign-cropper__zoom">
                    <Icon name="search" size="sm" color="secondary" />
                    <Slider
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.05}
                        aria-label="Zoom"
                        onChange={(_, value) => setZoom(value)}
                    />
                </div>
            </DialogContent>
            <DialogActions>
                <Button secondary outline small onClick={onCancel} disabled={isProcessing}>
                    Cancel
                </Button>
                <Button
                    primary
                    small
                    onClick={() => onApply(croppedAreaPixels)}
                    loading={isProcessing}
                    disabled={!croppedAreaPixels}
                >
                    <Icon name="save" size="sm" color="white" />
                    <span>Apply</span>
                </Button>
            </DialogActions>
        </Dialog>
    );
}

ImageCropModal.propTypes = {
    open: PropTypes.bool.isRequired,
    imageSrc: PropTypes.string,
    onCancel: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
    isProcessing: PropTypes.bool,
};

ImageCropModal.defaultProps = {
    imageSrc: null,
    isProcessing: false,
};

export default ImageCropModal;
